import { Component, computed, signal, ChangeDetectionStrategy } from "@angular/core";
import { DatePipe } from "@angular/common";
import { MatChipsModule } from "@angular/material/chips";

import type { ICellRendererAngularComp } from "ag-grid-angular";
import type { ICellRendererParams } from "ag-grid-community";

interface NdeData {
  lastNdeInspection?: string | Date | null;
  lastNdeInspectionDate?: string | Date | null;
  lastNdeDate?: string | Date | null;
}

@Component({
  selector: "nde-dates-cell-renderer",
  imports: [MatChipsModule, DatePipe],
  template: `
    @if (lastNdeDate(); as date) {
      <mat-chip-set
        role="list"
        aria-label="NDE Dates"
      >
        <mat-chip role="listitem">
          {{ date | date: "shortDate" }}
        </mat-chip>

        @if (sixMonthInspectionDate(); as sixMonthDate) {
          <mat-chip
            role="listitem"
            [class.two-weeks-out]="inspectionLessThan2Weeks()"
            [class.expired]="inspectionExpired()"
          >
            {{ sixMonthDate | date: "shortDate" }}
          </mat-chip>
        }
      </mat-chip-set>
    }
  `,
  changeDetection: ChangeDetectionStrategy.Eager,
  styles: [
    `
      :host {
        display: flex;
        align-items: center;
        width: 100%;
        height: 100%;
      }

      .two-weeks-out {
        background: yellow !important;
        background-color: yellow !important;
      }

      .expired {
        background: red !important;
        background-color: red !important;
        color: white !important;
      }
    `,
  ],
})
export class NdeDatesCellRenderer implements ICellRendererAngularComp {
  private readonly ndeData = signal<NdeData | null>(null);

  readonly lastNdeDate = computed(() => {
    const data = this.ndeData();
    if (!data) return null;
    const date = data.lastNdeInspection || data.lastNdeInspectionDate || data.lastNdeDate;
    if (!date) return null;
    return typeof date === "string" ? new Date(date) : date;
  });

  readonly sixMonthInspectionDate = computed(() => {
    const date = this.lastNdeDate();
    if (!date) return null;
    const nextDate = new Date(date);
    nextDate.setDate(nextDate.getDate() + 182.5);
    return nextDate;
  });

  readonly inspectionExpired = computed(() => {
    const nextNdeDate = this.sixMonthInspectionDate();
    if (!nextNdeDate) return false;
    const checkDate = new Date(nextNdeDate);
    checkDate.setHours(0, 0, 0, 0);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return today > checkDate;
  });

  readonly inspectionLessThan2Weeks = computed(() => {
    const nextNdeDate = this.sixMonthInspectionDate();
    if (!nextNdeDate) return false;
    const checkDate = new Date(nextNdeDate);
    checkDate.setHours(0, 0, 0, 0);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const twoWeeksFromNow = new Date(today);
    twoWeeksFromNow.setDate(twoWeeksFromNow.getDate() + 14);
    return checkDate <= twoWeeksFromNow && checkDate > today;
  });

  agInit(params: ICellRendererParams): void {
    this.ndeData.set(params.data as NdeData);
  }

  refresh(params: ICellRendererParams): boolean {
    this.ndeData.set(params.data as NdeData);
    return true;
  }
}
